import { BidderAddress } from '@/types/BidderAddresses';
import { createSelector } from '@reduxjs/toolkit';
import { dismissModal, openAddressVerifyModal } from '@/redux/modules/modal';
import { fetchBidderDataIfNeeded, getAuthToken } from './user';
import { getDeployment } from './config';
import { GlobalState } from '../rootReducer';
import {
    postAddBidderAddress,
    postUpdateBidderAddress,
    postUpdateProfile,
    UpdateProfileRequest,
} from '@/redux/api/bidder';

/* Action Types */
export const UPDATE_ACCOUNT_FAIL = 'la/ui/updateAccount/FAIL';
export const UPDATE_ACCOUNT_REQUEST = 'la/ui/updateAccount/REQUEST';
export const UPDATE_ACCOUNT_SUCCESS = 'la/ui/updateAccount/SUCCESS';
export const UPDATE_ACCOUNT_SUGGESTION = 'la/ui/updateAccount/SUGGESTION';
export const UPDATE_ACCOUNT_TOAST_MESSAGE = 'la/ui/updateAccount/TOAST_MESSAGE';

/* reducer */
export const DEFAULT_STATE = {
    error: false,
    errorMessage: null,
    submitted: false,
    success: false,
    toastMessage: '',
};

export type State = typeof DEFAULT_STATE & {
    errorMessage: string | null | undefined;
};

const pullMessages = (messages) => {
    return Object.keys(messages)
        .map((key) => messages[key])
        .join(' ');
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case UPDATE_ACCOUNT_FAIL:
            return {
                ...state,
                error: true,
                errorMessage: pullMessages(action.payload),
                submitted: false,
                success: false,
            };
        case UPDATE_ACCOUNT_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case UPDATE_ACCOUNT_SUCCESS:
            return {
                ...state,
                error: false,
                errorMessage: null,
                submitted: false,
                success: true,
            };
        case UPDATE_ACCOUNT_SUGGESTION:
            return {
                ...state,
                error: false,
                errorMessage: null,
                submitted: false,
                success: true,
            };
        case UPDATE_ACCOUNT_TOAST_MESSAGE:
            return {
                ...state,
                toastMessage: action.payload,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.updateAccount;

export const getUiUpdateAccount = createSelector(stateSelector, (state) => state);
export const getUpdateAccountToastMessage = createSelector(stateSelector, (state) => state.toastMessage);
export const getUpdateAccountLoading = createSelector(stateSelector, (state) =>
    Boolean(state.submitted && !state.success)
);

/* ACTION CREATORS */
export const updateAccountDetails =
    (updatedProfile: UpdateProfileRequest) => async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const response = await postUpdateProfile({
                authToken,
                deployment,
                profile: updatedProfile,
            });

            dispatch(dismissModal());

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                type: UPDATE_ACCOUNT_SUCCESS,
            });

            return dispatch(fetchBidderDataIfNeeded(null, true));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };

export const updateBidderAddress =
    (bidderID: number, address: BidderAddress & { validatorProvider?: string }, toastMessage: string, verify = false) =>
    async (dispatch: Function, getState: Function) => {
        try {
            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            const response = await postUpdateBidderAddress({
                address,
                authToken,
                bidderID,
                deployment,
                verify,
            });

            if (!Array.isArray(response.payload)) {
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: response,
                    type: UPDATE_ACCOUNT_SUGGESTION,
                });
                dispatch(
                    openAddressVerifyModal({
                        suggestedAddress: response.payload.suggestedAddress,
                        userEnteredAddress: address,
                    })
                );
                return;
            }

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                type: UPDATE_ACCOUNT_SUCCESS,
            });
            dispatch(dismissModal());

            if (Boolean(toastMessage)) {
                dispatch({
                    payload: toastMessage,
                    type: UPDATE_ACCOUNT_TOAST_MESSAGE,
                });
                dispatch({
                    payload: 0,
                    type: UPDATE_ACCOUNT_TOAST_MESSAGE,
                });
            }
            return dispatch(fetchBidderDataIfNeeded(null, true));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };

export const addBidderAddress =
    (bidderID: number, address: BidderAddress & { validatorProvider?: string }, verify = false) =>
    async (dispatch: Function, getState: Function) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const response = await postAddBidderAddress({
                address,
                authToken,
                bidderID,
                deployment,
                verify,
            });

            if (!Array.isArray(response.payload)) {
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: response,
                    type: UPDATE_ACCOUNT_SUGGESTION,
                });
                dispatch(
                    openAddressVerifyModal({
                        suggestedAddress: response.payload.suggestedAddress,
                        userEnteredAddress: address,
                    })
                );
                return;
            }

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                type: UPDATE_ACCOUNT_SUCCESS,
            });
            dispatch(dismissModal());
            return dispatch(fetchBidderDataIfNeeded(null, true));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };
